import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

// ** Icons
import PlayCircleOutline from "mdi-material-ui/PlayCircleOutline";
import CheckCircle from "mdi-material-ui/CheckCircle";
import ArrowRight from "mdi-material-ui/ArrowRight";

// ** Utils & sibling components
import CourseDrawer from "../CourseDrawer";
import { accessEnded } from "src/@core/utils/course-date";
import { pathWithPreservedSearch } from "src/@core/utils/urlQueryPreservation";
import { courseProgress } from "src/@core/utils/course-progress";

const defaultThumbnail = "/images/default-course.png";

/******************************************************************
 * ##: CourseCardV3
 * Modern (style-v3) course card for the learner home. Shows the
 * thumbnail (opens the details drawer on click), title, lesson
 * count, a progress bar and a full-width resume/start CTA.
 * @param {Object} data - one course record from /users/courses
 * @param {Boolean} loading - render the skeleton variant
 * @returns {JSX.Element} - a `.spv3-course` card
 * History:
 * 05-07-2026: Created
 ******************************************************************/
function CourseCardV3({ data, loading }) {
  const location = useLocation();
  const [progress, setProgress] = useState(0);
  const [detailsDrawer, setDetailsDrawer] = useState({ visible: false, data: null });

  useEffect(() => {
    if (data) setProgress(courseProgress(data));
  }, [data]);

  if (loading) return <CourseCardSkeleton />;

  const lessons = data?.courses_students?.lessons_count || data?.active_lessons_count || 0;
  const ended = accessEnded(data?.courses_students?.ended_at);
  const done = progress >= 100;
  const courseHref = pathWithPreservedSearch(`/courses/${data?.course_id}`, location.search);

  const ctaLabel = () => {
    if (ended) return "Acesso terminado";
    if (done) return "Rever curso";
    if (data?.courses_students?.started === true) return "Continuar a ver";
    return "Iniciar curso";
  };

  return (
    <article className={`spv3-course${done ? " spv3-course--done" : ""}`}>
      <div className="spv3-course__media" onClick={() => setDetailsDrawer({ visible: true, data })}>
        {done && (
          <span className="spv3-course__badge spv3-pill spv3-pill--success">
            <CheckCircle />
            Concluído
          </span>
        )}
        <img className="spv3-course__img" alt={data?.title} src={data?.image || defaultThumbnail} />
      </div>

      <div className="spv3-course__body">
        <h3 className="spv3-course__title">{data?.title}</h3>

        <div className="spv3-course__meta">
          <span className="spv3-chip">
            <PlayCircleOutline />
            {lessons === 1 ? "1 aula" : `${lessons} aulas`}
          </span>
        </div>

        <div className="spv3-course__progress">
          <div className="spv3-course__progress-top">
            <span className="spv3-course__progress-label">{done ? "Curso concluído" : "O teu progresso"}</span>
            <span className="spv3-course__pct">{progress}%</span>
          </div>
          <div className="spv3-course__track">
            <div className="spv3-course__fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="spv3-course__foot">
        {ended ? (
          <span className="spv3-btn spv3-btn--block is-disabled">{ctaLabel()}</span>
        ) : (
          <a className="spv3-btn spv3-btn--primary spv3-btn--block" href={courseHref}>
            {ctaLabel()}
            <ArrowRight className="spv3-btn__arrow" />
          </a>
        )}
      </div>

      <CourseDrawer
        visible={detailsDrawer.visible}
        data={detailsDrawer.data}
        close={() => setDetailsDrawer({ visible: false })}
      />
    </article>
  );
}

// HELPERS

/******************************************************************
 * ##: CourseCardSkeleton
 * Loading placeholder that mirrors the card layout so the grid
 * does not jump when the real courses arrive.
 * @returns {JSX.Element} - a shimmering `.spv3-course` placeholder
 * History:
 * 05-07-2026: Created
 ******************************************************************/
function CourseCardSkeleton() {
  return (
    <article className="spv3-course spv3-course--loading">
      <div className="spv3-course__media">
        <div className="spv3-skeleton spv3-sk-thumb" style={{ height: "100%" }} />
      </div>
      <div className="spv3-course__body">
        <div className="spv3-skeleton spv3-sk-line" style={{ width: "90%" }} />
        <div className="spv3-skeleton spv3-sk-line" style={{ width: "55%" }} />
        <div className="spv3-course__progress">
          <div className="spv3-skeleton spv3-sk-line" style={{ width: "40%", marginBottom: 10 }} />
          <div className="spv3-skeleton" style={{ height: 8, borderRadius: 999 }} />
        </div>
      </div>
      <div className="spv3-course__foot">
        <div className="spv3-skeleton" style={{ height: 46, borderRadius: 11 }} />
      </div>
    </article>
  );
}

export default CourseCardV3;
